<template>
  <div class="form-group">
    <label :for="id">{{ label + (rules ? '*' : '') || id }}</label>
    <ValidationProvider :name="label" :rules="rules" v-slot="{ errors }">
      <div class="position-relative">
      <textarea v-if="type == 'textarea'" :id="id" :value="value" @input="$emit('input', $event.target.value)" :class="{ 'form-control': true, 'is-invalid': hasErrors }" />
      <input
        v-if="(type == 'input' || type == 'date') && mask == null"
        :type="type"
        :id="id"
        :value="value"
        :maxlength="maxlength"
        @input="$emit('input', makeCapital($event.target.value))"
        @change="$emit('change', makeCapital($event.target.value))"
        @keyup.enter="$emit('keyup', arguments[0])"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc="$emit('keydown', arguments[0])"
        @blur="$emit('blur')"
        @focus="$emit('focus'), showSuggestions = true"
        :class="{ 'form-control': true, 'is-invalid': (hasErrors || errors[0]), 'datepick': isDate }"
        autocomplete="off"
        :required="required"
        @click="showSuggestions=true"
      />
      <ul v-if="filteredOptions.length && showSuggestions" class="card autocomplete-dropdown" ref="dropdownList">
        <li v-for="(option, index) in filteredOptions" :key="option.id || option.model" @mousedown.prevent="selectOption(option[optionLabel])" :class="{ 'bg-light': index === highlightedIndex }" ref="dropdownItem">
          {{ option[optionLabel] }}
        </li>
      </ul>
      <masked-input
        v-if="mask"
        :type="type"
        :id="id"
        v-model="maskedVal"
        :value="value"
        @input="$emit('input', pureVal)"
        @change="$emit('change', $event.target.value)"
        @keyup.enter="$emit('keyup', arguments[0])"
        :class="{ 'form-control': true, 'is-invalid': (hasErrors || errors[0])}"
        :mask="getMask"
        :placeholder="getPlaceholder"
        :guide="false"
        :required="required"
      >
      </masked-input>
      <span v-if="errors.length" class="invalid-feedback">
        <div v-for="error in errors" :key="error">{{ error }}</div>
      </span>
      </div>
    </ValidationProvider>
    <span v-if="hasErrors" class="invalid-feedback">
      <div v-for="error in errors" :key="error">{{ error }}</div>
    </span>
  </div>
</template>
<script>
import MaskedInput from 'vue-text-mask';
import { conformToMask } from 'vue-text-mask';
import emailMasks from 'text-mask-addons/dist/emailMask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

export default {
  components: {
    MaskedInput,
  },
  data() {
    return {
      emailMask: emailMasks,
      maskedVal: null,
      showSuggestions: false,
      highlightedIndex: -1
    };
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionLabel: {
      type: String,
      default: 'name'
    },
    type: {
      type: String,
      default: 'input',
      validator: value => ['input', 'textarea', 'date'].includes(value),
    },
    mask: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    maxlength: {
      type: String,
      default: null,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    rules: {
      type: String,
      default: null,
    },
    textTransform: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  watch: {
    value(newValue) {
      if (newValue != null && newValue != '') {
        this.maskedVal = this.toMask(newValue);
      }
    },
    filteredOptions() {
      this.highlightedIndex = 0;
      this.$nextTick(() => {
        this.scrollHighlightedIntoView();
      });
    }
  },
  methods: {
    moveDown() {
      if (!this.filteredOptions.length) return;
      if (this.highlightedIndex < this.filteredOptions.length - 1) {
        this.highlightedIndex++;
        this.scrollHighlightedIntoView();
      }
    },
    moveUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
        this.scrollHighlightedIntoView();
      }
    },
    selectHighlighted() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
        const option = this.filteredOptions[this.highlightedIndex];
        this.selectOption(option[this.optionLabel]);
      }
    },
    scrollHighlightedIntoView() {
      this.$nextTick(() => {
        const dropdownList = this.$refs.dropdownList;
        const items = this.$refs.dropdownItem;
        const item = Array.isArray(items) ? items[this.highlightedIndex] : items;
        if (item && dropdownList && typeof item.offsetTop !== 'undefined') {
          item.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      });
    },
    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.showSuggestions = false;
      }
    },
    makeCapital(str) {
      if (this.textTransform == 'capitalize') {
        return str.replace(/(^|\s)\S/g, l => l.toUpperCase());
      }
      if (this.textTransform == 'uc') {
        return str.toUpperCase();
      }
      return str;
    },
    toMask(val) {
      if (this.mask == 'price') {
        var rawPrice = val;
        var formattedPrice = Number(rawPrice).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        });
        return formattedPrice;
      }
      if (this.mask == 'email') {
        return val;
      }
      if (this.mask != 'email' && this.mask != null) {
        var conformed = conformToMask(val, this.getMask, { guide: false });
        return conformed.conformedValue;
      }
      return val;
    },
    selectOption(option) {
      this.$emit('input', option);
      this.showSuggestions = false;
    },
  },
  computed: {
    filteredOptions() {
      if (!this.options.length || !this.value) return this.options;
      return this.options.filter(option =>
        String(option[this.optionLabel]).toLowerCase().includes(this.value.toLowerCase())
      );
    },
    isDate() {
      if (this.type == 'date') {
        return true;
      }
      return false;
    },
    pureVal() {
      if (this.mask == 'email') {
        return this.maskedVal;
      }
      return this.maskedVal.replace(/\$/g, '').replace(/,/g, '').replace(/\(/g, '').replace(/\)/g, '').replace('-', '').replace(/_/g, '').replace(/ /g, '');
    },
    hasErrors() {
      return this.errors && this.errors.length > 0;
    },
    getPlaceholder() {
      if (this.mask == 'email') {
        return 'info@example.com';
      }
      if (this.mask == 'price') {
        return '$1,000';
      }
      if (this.mask == 'phone') {
        return '(555) 555-5555';
      }
      if (this.mask == 'cc') {
        return '5424 0000 0000 0015';
      }
      if (this.mask == 'exp') {
        return '07/24';
      }
      if (this.mask == 'cvv') {
        return '123';
      }
      return '';
    },
    getMask() {
      if (this.mask == 'email') {
        return this.emailMask;
      }
      if (this.mask == 'price') {
        return createNumberMask({ prefix: '$', allowDecimal: true });
      }
      if (this.mask == 'phone') {
        return ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
      }
      if (this.mask == 'cc') {
        return [/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/];
      }
      if (this.mask == 'exp') {
        return [/[0-9]/, /[0-9]/, '/', /[0-9]/, /[0-9]/];
      }
      if (this.mask == 'cvv') {
        return [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];
      }
      return false;
    },
  },
  async mounted() {
    document.addEventListener('click', this.handleClickOutside);
    if (this.value) {
      if (this.mask == 'phone' && String(this.value).includes('+1')) {
        this.maskedVal = this.toMask(String(this.value).replace('+1', ''));
      } else {
        this.maskedVal = this.toMask(this.value);
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style lang="scss" scoped>
textarea {
  height: 120px;
}

.datepick {
  position: relative;
}

input[type='date']::-webkit-calendar-picker-indicator {
  background: transparent;
  bottom: 0;
  color: transparent;
  cursor: pointer;
  height: auto;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: auto;
}

.autocomplete-dropdown {
  position: absolute;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  min-width: 100%;
  width: max-content;
  display: inline-block;
  white-space: nowrap;
}

.autocomplete-dropdown li {
  padding: 6px 12px;
  cursor: pointer;
  white-space: nowrap;
}

.autocomplete-dropdown li:hover {
  background-color: rgba(24, 28, 33, 0.06) !important;
}

.dark-style .autocomplete-dropdown li:hover {
  background-color: #3e4147 !important;
}
</style>